import './FileUploader.scss'

import React, { useCallback, useEffect, useRef, useState } from 'react'

import Button from '../Button/Button'
import Icon from '../Icon/Icon'
import classNames from 'classnames'
import { formEvent } from '../Form/Form'
import { getFileData } from '../../helpers/upload'
import t from '../../helpers/i18n'
import { useDropzone } from 'react-dropzone'

const FileUploader = ({ name, label, filename, source, required }) => {
  const [file, setFile] = useState()
  const [deleted, setDeleted] = useState(false)
  const element = useRef()

  const propagateValue = useCallback(() => {
    const fileType =
      file && file.type && file.type.length > 0
        ? file.type
        : 'application/octet-stream'
    const fileData = file
      ? { name: file.name, type: fileType, size: file.size }
      : undefined
    if (name) {
      element.current.dispatchEvent(formEvent({
        action: 'PENDING',
        name,
        value: file ? { ...fileData, get: async () => getFileData(file) } : deleted ? { delete: true } : null
      }))
    }
  }, [name, file, deleted])

  const onDrop = useCallback(
    (acceptedFiles) => {
      setFile(acceptedFiles[0])
    },
    [setFile]
  )

  const {getRootProps, getInputProps} = useDropzone({ onDrop })
  const inputProps = getInputProps()

  const handleSelect = useCallback(() => inputProps.ref.current.click(), [inputProps])
  const handleClear = useCallback(() => {
    inputProps.ref.current.value = ""
    setFile(false)
    setDeleted(true)
  }, [inputProps.ref])
  const handleRestore = useCallback((event) => {
    event.stopPropagation()
    inputProps.ref.current.value = ""
    setFile(false)
    setDeleted(false)
  }, [inputProps.ref])

  useEffect(propagateValue, [propagateValue])

  useEffect(() => {
    setFile(undefined)
  }, [source])

  useEffect(() => {
    if (name) {
      element.current.dispatchEvent(
        formEvent({
          action: 'REQUIRED',
          name,
          required
        })
      )
    }
  }, [name, required])

  useEffect(() => {
    const _element = element.current
    return () => {
      if (name) {
        _element.dispatchEvent(
          formEvent({
            action: 'REMOVE',
            name
          })
        )
      }
    }
  }, [name])

  return (
    <div className="file-uploader" ref={element}>
      <label className="text-label label-small-medium">{label}</label>
      <div className="-container">
        {filename && source && !file && !deleted &&
            <div className="-preview">
              <a className="-button" href={source} target="_new"><Icon size={16} icon='DownloadIcon' /> {filename}</a>
              <div className="-buttons">
                <Button type="light" size="small" color="green" label={t("buttons.Select_another_file")} onClick={handleSelect} />
                <Button type="light" size="small" color="neutral" label={t("buttons.Remove")} onClick={handleClear} />
              </div>
            </div>
          }
        <div className={classNames("-dropzone", { "-hidden": filename && source && !file && !deleted })} {...getRootProps()}>
          <div className="-hint label-small-medium">{t('Drag_the_new_file_to_this_area')}</div>
          <input {...inputProps} />
          {file && file.name &&
            <div className="-new">
              <Icon size={14} icon='UploadIcon' />
              <div className="-name label-small-medium">{file.name}</div>
            </div>
          }
          <div className="-buttons">
            <Button type="light" size="small" color="green" label={t("buttons.Select_File")} />
            <Button type="light" size="small" color="neutral" label={t("buttons.Restore")} onClick={handleRestore} />
          </div>
        </div>
      </div>
      {(!source || deleted) && !file && required && (
        <span className={classNames('-error', 'color-error', 'caption-tiny-basic')}>{t('validations.Required')}</span>
      )}
    </div>
  )
}

export default FileUploader
